/**
 * Bid-Job Management Page
 *
 * Admin page to manage bid-job numbers and descriptions per company.
 */

import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthProvider';
import { fetchWithAuth, getResources, getActiveCompanies } from '../services/api';
import { Resource } from '../services/types';

interface BidJob {
  id: string;
  companyId: number;
  companyName: string;
  bidJobNumber: string;
  bidJobDescription: string;
  isActive: boolean;
  isCommon: boolean;
  isDefault: boolean;
  defaultForResourceId?: number;
  defaultForResourceName?: string;
}

interface Company {
  id: number;
  companyName: string;
}

export default function BidJobAdmin() {
  const { user } = useAuth();
  const [bidJobs, setBidJobs] = useState<BidJob[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | 'all' | 'common'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingBidJob, setEditingBidJob] = useState<BidJob | null>(null);
  const [formData, setFormData] = useState({
    companyId: 0,
    companyName: '',
    bidJobNumber: '',
    bidJobDescription: '',
    isCommon: false,
    isDefault: false,
    defaultForResourceId: null as number | null,
    defaultForResourceName: '',
  });
  const [saving, setSaving] = useState(false);

  const loadBidJobs = useCallback(async () => {
    try {
      setLoading(true);
      // fetchWithAuth already unwraps { success, data } and returns just the data array
      const bidJobsData = await fetchWithAuth<BidJob[]>('/bidjobs');
      setBidJobs(bidJobsData);
    } catch (err) {
      setError('Failed to load bid-jobs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load ALL companies from API for the dropdown
  const loadCompanies = useCallback(async () => {
    try {
      const companiesData = await getActiveCompanies();
      setCompanies(
        companiesData
          .map((c) => ({ id: c.autoTaskId, companyName: c.companyName }))
          .sort((a, b) => a.companyName.localeCompare(b.companyName))
      );
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  }, []);

  useEffect(() => {
    loadBidJobs();
    loadCompanies();
  }, [loadBidJobs, loadCompanies]);

  // Load resources (techs) for the default-for selector
  useEffect(() => {
    async function loadResourceList() {
      try {
        const data = await getResources();
        // Filter to only actual technicians (exclude API users, system accounts)
        const filteredResources = data.filter(r =>
          r.isActive &&
          r.licenseType !== 3 &&
          r.autoTaskId !== 4 &&
          !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
        );
        setResources(filteredResources);
      } catch (err) {
        console.error('Failed to load resources:', err);
      }
    }
    loadResourceList();
  }, []);

  const filteredBidJobs = bidJobs.filter((bj) => {
    // Filter by company
    if (selectedCompanyId === 'common' && !bj.isCommon) return false;
    if (selectedCompanyId !== 'all' && selectedCompanyId !== 'common' && bj.companyId !== selectedCompanyId) return false;

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        bj.bidJobNumber.toLowerCase().includes(term) ||
        bj.bidJobDescription.toLowerCase().includes(term) ||
        bj.companyName.toLowerCase().includes(term)
      );
    }

    return true;
  });

  // Group by company for display
  const groupedBidJobs = filteredBidJobs.reduce((acc, bj) => {
    const key = bj.isCommon ? '(Common - All Companies)' : bj.companyName;
    if (!acc[key]) acc[key] = [];
    acc[key].push(bj);
    return acc;
  }, {} as Record<string, BidJob[]>);

  const openAddModal = (companyId?: number, companyName?: string) => {
    setEditingBidJob(null);
    setFormData({
      companyId: companyId || 0,
      companyName: companyName || '',
      bidJobNumber: '',
      bidJobDescription: '',
      isCommon: false,
      isDefault: false,
      defaultForResourceId: null,
      defaultForResourceName: '',
    });
    setShowModal(true);
  };

  const openEditModal = (bj: BidJob) => {
    setEditingBidJob(bj);
    setFormData({
      companyId: bj.companyId,
      companyName: bj.companyName,
      bidJobNumber: bj.bidJobNumber,
      bidJobDescription: bj.bidJobDescription,
      isCommon: bj.isCommon,
      isDefault: bj.isDefault || false,
      defaultForResourceId: bj.defaultForResourceId || null,
      defaultForResourceName: bj.defaultForResourceName || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.bidJobNumber || !formData.bidJobDescription) {
      alert('Bid-Job Number and Description are required');
      return;
    }

    if (!formData.isCommon && !formData.companyId) {
      alert('Please select a company or mark as common');
      return;
    }

    setSaving(true);
    try {
      if (editingBidJob) {
        // Update
        await fetchWithAuth(`/bidjobs/${editingBidJob.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
      } else {
        // Create
        await fetchWithAuth('/bidjobs', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
      }
      setShowModal(false);
      loadBidJobs();
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save bid-job');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bj: BidJob) => {
    if (!confirm(`Delete bid-job "${bj.bidJobNumber} - ${bj.bidJobDescription}"?`)) {
      return;
    }

    try {
      await fetchWithAuth(`/bidjobs/${bj.id}`, { method: 'DELETE' });
      loadBidJobs();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete bid-job');
    }
  };

  return (
    <>
      <PageHeader userEmail={user?.email} />

      <div className="max-w-6xl mx-auto py-6 px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Bid-Job Management</h1>
            <p className="text-slate-400 mt-1">Manage bid-job numbers and descriptions per company</p>
          </div>
          <button
            onClick={() => openAddModal()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Bid-Job
          </button>
        </div>

        {/* Filters */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-slate-400 mb-1">Filter by Company</label>
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value === 'all' ? 'all' : e.target.value === 'common' ? 'common' : parseInt(e.target.value))}
                className="w-full bg-slate-700 text-white rounded px-3 py-2 border border-slate-600"
              >
                <option value="all">All Companies</option>
                <option value="common">Common (All Companies)</option>
                <optgroup label="Companies">
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-slate-400 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by number or description..."
                className="w-full bg-slate-700 text-white rounded px-3 py-2 border border-slate-600"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-slate-400 text-center py-8">Loading...</div>
        ) : error ? (
          <div className="text-red-400 text-center py-8">{error}</div>
        ) : filteredBidJobs.length === 0 ? (
          <div className="text-slate-400 text-center py-8">
            No bid-jobs found. Click "Add Bid-Job" to create one.
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedBidJobs)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([companyName, jobs]) => (
                <div key={companyName} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                  <div className="bg-slate-700/50 px-4 py-3 flex items-center justify-between">
                    <h3 className="text-white font-semibold">{companyName}</h3>
                    <span className="text-slate-400 text-sm">{jobs.length} bid-job{jobs.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="divide-y divide-slate-700">
                    {jobs.map((bj) => (
                      <div key={bj.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-700/30">
                        <div>
                          <span className="text-blue-400 font-mono">{bj.bidJobNumber}</span>
                          <span className="text-slate-400 mx-2">|</span>
                          <span className="text-white">{bj.bidJobDescription}</span>
                          {bj.isCommon && (
                            <span className="ml-2 text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded">
                              Common
                            </span>
                          )}
                          {bj.isDefault && (
                            <span className="ml-2 text-xs bg-green-600/30 text-green-300 px-2 py-0.5 rounded">
                              Default{bj.defaultForResourceName ? ` for ${bj.defaultForResourceName}` : ''}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(bj)}
                            className="text-slate-400 hover:text-white p-1"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(bj)}
                            className="text-slate-400 hover:text-red-400 p-1"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">
              {editingBidJob ? 'Edit Bid-Job' : 'Add Bid-Job'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Company</label>
                <select
                  value={formData.isCommon ? 'common' : formData.companyId || ''}
                  onChange={(e) => {
                    if (e.target.value === 'common') {
                      setFormData({ ...formData, isCommon: true, companyId: 0, companyName: '(Common)' });
                    } else {
                      const company = companies.find((c) => c.id === parseInt(e.target.value));
                      setFormData({
                        ...formData,
                        isCommon: false,
                        companyId: parseInt(e.target.value),
                        companyName: company?.companyName || '',
                      });
                    }
                  }}
                  className="w-full bg-slate-700 text-white rounded px-3 py-2 border border-slate-600"
                >
                  <option value="">Select Company...</option>
                  <option value="common">Common (All Companies)</option>
                  <optgroup label="Companies">
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.companyName}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Bid-Job Number</label>
                <input
                  type="text"
                  value={formData.bidJobNumber}
                  onChange={(e) => setFormData({ ...formData, bidJobNumber: e.target.value })}
                  placeholder="e.g., 1234 or HD"
                  className="w-full bg-slate-700 text-white rounded px-3 py-2 border border-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.bidJobDescription}
                  onChange={(e) => setFormData({ ...formData, bidJobDescription: e.target.value })}
                  placeholder="e.g., Help Desk, Server Maintenance"
                  className="w-full bg-slate-700 text-white rounded px-3 py-2 border border-slate-600"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-green-500"
                />
                <label htmlFor="isDefault" className="text-sm text-slate-300">
                  Set as default for {formData.isCommon ? 'all companies' : 'this company'}
                </label>
              </div>

              {/* Tech-specific default selector - only show when isDefault is checked */}
              {formData.isDefault && (
                <div className="ml-6 p-3 bg-slate-700/50 rounded border border-slate-600">
                  <label className="block text-sm text-slate-400 mb-2">Default for specific tech (optional)</label>
                  <select
                    value={formData.defaultForResourceId || ''}
                    onChange={(e) => {
                      const resourceId = e.target.value ? parseInt(e.target.value) : null;
                      const resource = resources.find(r => r.autoTaskId === resourceId);
                      setFormData({
                        ...formData,
                        defaultForResourceId: resourceId,
                        defaultForResourceName: resource ? `${resource.firstName} ${resource.lastName}` : '',
                      });
                    }}
                    className="w-full bg-slate-700 text-white rounded px-3 py-2 border border-slate-600"
                  >
                    <option value="">Everyone (general default)</option>
                    {resources.map((r) => (
                      <option key={r.autoTaskId} value={r.autoTaskId}>
                        {r.firstName} {r.lastName}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.defaultForResourceId
                      ? `This bid-job will be the default only for ${formData.defaultForResourceName}`
                      : 'Leave as "Everyone" to make this the general default'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
